import { useState, useCallback, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { ListPagination } from '@/components/ListPagination';
import { VehiclePipelineStages, type PipelineStage } from '@/components/vehicles/VehiclePipeline';
import { cn } from '@/lib/utils';
import {
  Phone,
  MapPin,
  Calendar,
  CheckCircle2,
  Loader2,
  X,
  StickyNote,
  User,
  Mail,
  Building2,
  Briefcase,
  Car,
  Target,
  Clock,
  MessageSquare,
  Info,
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import {
  listPartnerApplications,
  markPartnerApplicationContacted,
  undoPartnerApplicationContacted,
  addPartnerApplicationNote,
  type PartnerApplication,
} from '@/integrations/supabase/website';

// ─── Stage config ─────────────────────────────────────────────────────────────

type AppStage = 'new' | 'noted' | 'contacted';

const STAGE_CONFIG: { key: AppStage; label: string; color: string }[] = [
  { key: 'new',       label: 'New',       color: 'blue' },
  { key: 'noted',     label: 'Noted',     color: 'amber' },
  { key: 'contacted', label: 'Contacted', color: 'green' },
];

const STAGE_BADGE_STYLES: Record<AppStage, string> = {
  new:       'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-400',
  noted:     'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-400',
  contacted: 'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950/40 dark:text-green-400',
};

const STAGE_LABELS: Record<AppStage, string> = {
  new: 'New', noted: 'Noted', contacted: 'Contacted',
};

const ITEMS_PER_PAGE = 15;

// ─── Status Badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const style = STAGE_BADGE_STYLES[status as AppStage];
  const label = STAGE_LABELS[status as AppStage];
  if (style && label) return <Badge variant="outline" className={style}>{label}</Badge>;
  return <Badge variant="outline">{status}</Badge>;
}

// ─── Detail Field ─────────────────────────────────────────────────────────────

function DetailField({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2.5">
      <Icon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">{label}</p>
        <p className="text-sm mt-0.5">{value}</p>
      </div>
    </div>
  );
}

// ─── Detail Sheet ─────────────────────────────────────────────────────────────

function ApplicationDetailSheet({
  application,
  open,
  onOpenChange,
}: {
  application: PartnerApplication | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [noteText, setNoteText] = useState(application?.notes || '');

  useEffect(() => {
    setNoteText(application?.notes || '');
  }, [application?.id, application?.notes]);

  const contactMutation = useMutation({
    mutationFn: async () => {
      const result = await markPartnerApplicationContacted(application!.id);
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    onSuccess: () => {
      toast.success('Marked as contacted');
      queryClient.invalidateQueries({ queryKey: ['partner-applications'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const undoContactMutation = useMutation({
    mutationFn: async () => {
      const result = await undoPartnerApplicationContacted(application!.id);
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    onSuccess: () => {
      toast.success('Undo successful');
      queryClient.invalidateQueries({ queryKey: ['partner-applications'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const noteMutation = useMutation({
    mutationFn: async () => {
      const result = await addPartnerApplicationNote(application!.id, noteText);
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    onSuccess: () => {
      toast.success('Note saved');
      queryClient.invalidateQueries({ queryKey: ['partner-applications'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!application) return null;

  const a = application;
  const fullName = `${a.first_name} ${a.last_name}`.trim();
  const hasExistingNote = !!a.notes;
  const noteChanged = noteText.trim() !== (a.notes || '');

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="!inset-y-4 !right-4 !h-auto !p-0 !w-[85vw] !max-w-3xl rounded-2xl border shadow-2xl bg-background overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="p-6 border-b bg-muted/30 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
          <div className="relative z-10 flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Building2 className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-semibold tracking-tight">{fullName}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <StatusBadge status={a.status} />
                  <span className="text-xs text-muted-foreground">
                    Applied {format(new Date(a.created_at), 'MMM d, yyyy')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <ScrollArea className="flex-1 min-h-0">
          <div className="p-6 space-y-6">
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Contact Information</h3>
              <div className="rounded-lg border bg-muted/20 p-4 space-y-1">
                <DetailField icon={Phone} label="Phone" value={a.phone} />
                <DetailField icon={Mail} label="Email" value={a.email} />
                <DetailField icon={MapPin} label="Location" value={a.location} />
              </div>
            </div>

            <div>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Business Details</h3>
              <div className="rounded-lg border bg-muted/20 p-4 space-y-1">
                <DetailField icon={Building2} label="Entity Type" value={a.entity_type} />
                <DetailField icon={Briefcase} label="Partnership Type" value={a.partnership_type} />
                <DetailField icon={Briefcase} label="Has Experience" value={a.has_experience} />
              </div>
            </div>

            <div>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Vehicle Interests</h3>
              <div className="rounded-lg border bg-muted/20 p-4 space-y-1">
                <DetailField icon={Car} label="Vehicle Count" value={a.vehicle_count_range} />
                <DetailField icon={Car} label="Vehicle Type" value={a.vehicle_type} />
                <DetailField icon={Clock} label="Buy Start Time" value={a.buy_start_time} />
              </div>
            </div>

            <div>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Goals & Preferences</h3>
              <div className="rounded-lg border bg-muted/20 p-4 space-y-1">
                <DetailField icon={Target} label="Main Goal" value={a.main_goal} />
                <DetailField icon={Target} label="Comfort / Performance" value={a.comfort_performance} />
                <DetailField icon={Info} label="Additional Info" value={a.additional_info} />
                <DetailField icon={MessageSquare} label="Referral" value={a.referral} />
              </div>
            </div>

            {/* Note Section */}
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Admin Note</h3>
              <div className="space-y-2">
                <Textarea
                  placeholder="Add a note about this applicant..."
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  rows={3}
                  className="resize-none"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => noteMutation.mutate()}
                  disabled={!noteChanged || noteMutation.isPending}
                >
                  {noteMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  ) : (
                    <StickyNote className="h-4 w-4 mr-1.5" />
                  )}
                  {hasExistingNote ? 'Save Note' : 'Add Note'}
                </Button>
              </div>
            </div>

            {a.contacted_at && (
              <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                <CheckCircle2 className="h-4 w-4" />
                Contacted on {format(new Date(a.contacted_at), 'MMM d, yyyy \'at\' h:mm a')}
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Footer */}
        <div className="p-4 border-t bg-background/80 backdrop-blur-md flex items-center justify-between">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">
            Close
          </Button>

          {a.status === 'contacted' ? (
            <Button
              variant="outline"
              onClick={() => undoContactMutation.mutate()}
              disabled={undoContactMutation.isPending}
              className="rounded-full"
            >
              {undoContactMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <X className="h-4 w-4 mr-1.5" />
              )}
              Undo Contacted
            </Button>
          ) : (
            <Button
              onClick={() => contactMutation.mutate()}
              disabled={contactMutation.isPending}
              className="rounded-full shadow-lg hover:shadow-primary/25 transition-all"
            >
              {contactMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4 mr-1.5" />
              )}
              Mark Contacted
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function PartnerApplications() {
  const [stageFilter, setStageFilter] = useState<AppStage | null>('new');
  const [page, setPage] = useState(1);
  const [selectedApp, setSelectedApp] = useState<PartnerApplication | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Fetch ALL applications across every page so the stage counts, filtering and
  // pagination reflect the full set — not just the first server page (the proxy
  // paginates at 20/page).
  const { data: allResult, isLoading, error } = useQuery({
    queryKey: ['partner-applications'],
    queryFn: async () => {
      const all: PartnerApplication[] = [];
      let page = 1;
      let count = 0;
      for (;;) {
        const result = await listPartnerApplications(page);
        if (result.error) throw new Error(result.error);
        const rows = result.data?.data ?? [];
        count = result.data?.count ?? count;
        all.push(...rows);
        if (rows.length === 0 || all.length >= count) break;
        page++;
      }
      return { data: all, count };
    },
  });

  const allApplications = allResult?.data || [];
  const totalCount = allResult?.count || 0;

  const stageCounts = useMemo(() => {
    const counts: Record<AppStage, number> = { new: 0, noted: 0, contacted: 0 };
    allApplications.forEach((app) => {
      const s = app.status as AppStage;
      if (s in counts) counts[s]++;
    });
    return counts;
  }, [allApplications]);

  const pipelineStages: PipelineStage[] = STAGE_CONFIG.map((s) => ({
    ...s,
    count: stageCounts[s.key] || 0,
  }));

  const filteredApplications = useMemo(() => {
    if (stageFilter === null) return allApplications;
    return allApplications.filter((app) => app.status === stageFilter);
  }, [allApplications, stageFilter]);

  const totalFiltered = filteredApplications.length;
  const totalPages = Math.ceil(totalFiltered / ITEMS_PER_PAGE);
  const paginatedApplications = filteredApplications.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  const handleRowClick = useCallback((app: PartnerApplication) => {
    setSelectedApp(app);
    setSheetOpen(true);
  }, []);

  const handleStageClick = useCallback((key: string | null) => {
    setStageFilter(key as AppStage | null);
    setPage(1);
  }, []);

  const currentSelectedApp = selectedApp
    ? allApplications.find((a) => a.id === selectedApp.id) || selectedApp
    : null;

  return (
    <div className="space-y-6 animate-fade-in px-4 sm:px-6 lg:px-8 pt-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight mb-2">Partner Applications</h1>
          <p className="text-muted-foreground">Review and manage partner applications from the website.</p>
        </div>
      </div>

      {/* Staged view + Table */}
      <Card className="border-0">
        <CardContent className="space-y-4">
          <VehiclePipelineStages
            stages={pipelineStages}
            activeStage={stageFilter}
            onStageClick={handleStageClick}
            groupCount={totalCount}
          />

          {error ? (
            <div className="text-center py-12 text-destructive">
              <p className="text-sm font-medium">Failed to load applications</p>
              <p className="text-xs text-muted-foreground mt-1">{(error as Error).message}</p>
            </div>
          ) : isLoading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : (
            <>
              <ListPagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={totalFiltered}
                onPageChange={setPage}
              />
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableTableHead>Name</SortableTableHead>
                      <SortableTableHead>Phone</SortableTableHead>
                      <SortableTableHead>Entity</SortableTableHead>
                      <SortableTableHead>Location</SortableTableHead>
                      <SortableTableHead>Partnership</SortableTableHead>
                      <SortableTableHead>Status</SortableTableHead>
                      <SortableTableHead>Applied</SortableTableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedApplications.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                          {stageFilter
                            ? `No ${STAGE_LABELS[stageFilter].toLowerCase()} applications.`
                            : 'No applications have been submitted yet.'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedApplications.map((app) => (
                        <TableRow
                          key={app.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => handleRowClick(app)}
                        >
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                <span className="text-xs font-medium text-primary">
                                  {app.first_name[0]}{app.last_name[0]}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium">{app.first_name} {app.last_name}</p>
                                <p className="text-xs text-muted-foreground">{app.email}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">{app.phone}</TableCell>
                          <TableCell className="text-sm">{app.entity_type}</TableCell>
                          <TableCell className="text-sm">{app.location}</TableCell>
                          <TableCell className="text-sm">{app.partnership_type}</TableCell>
                          <TableCell>
                            <StatusBadge status={app.status} />
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {format(new Date(app.created_at), 'MMM d, yyyy')}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Detail Sheet */}
      <ApplicationDetailSheet
        application={currentSelectedApp}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
      />
    </div>
  );
}
